import React from 'react';

export const EVENTS = {
  SEARCH_INPUT_CHANGE: 'SEARCH_INPUT_CHANGE',
  FETCH_LIST_STARTED: 'FETCH_LIST_STARTED',
  FETCH_LIST_SUCCESS: 'FETCH_LIST_SUCCESS',
  FETCH_LIST_FAILED: 'FETCH_LIST_FAILED',
  FETCH_CONTENT_STARTED: 'FETCH_CONTENT_STARTED',
  FETCH_CONTENT_SUCCESS: 'FETCH_CONTENT_SUCCESS',
  FETCH_CONTENT_FAILED: 'FETCH_CONTENT_FAILED',
  SELECT_NEW_CATEGORY: 'SELECT_NEW_CATEGORY',
  SELECT_GLOSSARY_LIST_ITEM: 'SELECT_GLOSSARY_LIST_ITEM',
  TOGGLE_GLOSSARY_INDEX_COLLAPSE: 'TOGGLE_GLOSSARY_INDEX_COLLAPSE',
  SELECT_GLOSSARY_TOOLTIP_ITEM: 'SELECT_GLOSSARY_TOOLTIP_ITEM',
};

const INITIAL_STATE = {
  searchText: '',
  indexCollapsed: false,
  selected: '',
  categories: [],
  listApi: {loading: null, error: null},
  glossaryList: [],
  contentApi: {loading: null, error: null},
  glossaryItem: {},
};

function glossaryReducer(state, action) {
  switch (action.type) {
    case EVENTS.SEARCH_INPUT_CHANGE:
      return {...state, searchText: action.payload};
    case EVENTS.FETCH_LIST_STARTED:
      return {...state, listApi: {error: false, loading: true}};
    case EVENTS.FETCH_LIST_SUCCESS:
      return handleFetchListSuccess(state, action.payload);
    case EVENTS.FETCH_LIST_FAILED:
      return {...state, listApi: {loading: false, error: true}};
    case EVENTS.FETCH_CONTENT_STARTED:
      return {
        ...state,
        contentApi: {...state.contentApi, loading: true},
        //indexCollapsed: state.searchText != '',
      };
    case EVENTS.FETCH_CONTENT_SUCCESS:
      return {
        ...state,
        contentApi: {loading: false, error: false},
        glossaryItem: action.payload,
      };
    case EVENTS.FETCH_CONTENT_FAILED:
      return {...state, contentApi: {loading: false, error: true}};
    case EVENTS.SELECT_NEW_CATEGORY:
      return updateSelectedCategory(state, action.payload);
    case EVENTS.SELECT_GLOSSARY_LIST_ITEM:
      return updateGlossaryListItemSelection(state, action.payload);
    case EVENTS.SELECT_GLOSSARY_TOOLTIP_ITEM:
      return updateGlossaryTooltipItemSelection(state, action.payload);
    case EVENTS.TOGGLE_GLOSSARY_INDEX_COLLAPSE:
      return toggleGlossaryIndex(state);
    default:
      return {...state};
  }
}

function toggleGlossaryIndex(state) {
  return {
    ...state,
    indexCollapsed: !state.indexCollapsed,
  };
}

function handleFetchListSuccess(state, payload) {
  const listApi = {loading: false, error: false};
  let glossaryList = payload;
  if (glossaryList.length) {
    glossaryList = updateListItemSelected(glossaryList, glossaryList[1]?.id);
  }
  const indexCollapsed = false;
  return {...state, listApi, glossaryList, indexCollapsed, selected: glossaryList[1]?.id};
}

function updateListItemSelected(glossaryList, selectedId) {
  return glossaryList.map((listItem) => {
    const newItem = {...listItem, selected: false};
    if (newItem.id === selectedId) {
      newItem.selected = true;
    }
    return newItem;
  });
}

function updateSelectedCategory(state, payload) {
  const categories = state.categories.map((listItem) => {
    const newItem = {...listItem, selected: false};
    if (newItem.value === payload) {
      newItem.selected = true;
    }
    return newItem;
  });
  const searchText = '';
  return {...state, categories, searchText};
}

function updateGlossaryListItemSelection(state, payload) {

  const glossaryList = state.glossaryList.map((listItem) => {
    const newItem = {...listItem, selected: false};
    if (newItem.id === payload) {
      newItem.selected = true;
    }
    return newItem;
  });
  const indexCollapsed = false;
  const searchText = undefined;
  return {...state, searchText, glossaryList, indexCollapsed, selected: payload};
}

function updateGlossaryTooltipItemSelection(state, payload) {
  const glossaryList = state.glossaryList.map((listItem) => {
    const newItem = {...listItem, selected: false};
    if (newItem.id === payload.id) {
      newItem.selected = true;
    }
    return newItem;
  });
  const indexCollapsed = true;
  const searchText = undefined;
  return {...state, searchText, glossaryList, indexCollapsed, selected: payload.id};
}

/**
 * useGlossaryHook :
 *  - The custom hook that holds the data for Glossary and Handles all its
 *    top level opeartions and logic.
 */
export function useGlossaryHook(props) {
  const [state, dispatch] = React.useReducer(glossaryReducer, {
    ...INITIAL_STATE,
    searchText: props.searchText,
    categories: props.categories,
  });

  /**
   * When You update Selected Categories or Update the list -> fetchGlossaryList
   */
  React.useEffect(() => {
    if (state.categories) {
      onFetchGlossary();
    }
  }, [state.categories]);

  /**
   * When you update the glossary List; Likely a new item is selected
   * This makes us fetch the selected GlossaryItem Again.
   */
  React.useEffect(() => {
    if (state.glossaryList && state.glossaryList.length) {
      onFetchGlossaryItem(state.selected);
    }
  }, [state.glossaryList]);

  /**
   * To get params for lising Glossary Items
   */
  const getFetchGlossaryParams = () => {
    const params = {};
    const selectedCategory = state.categories.find(
      (category) => category.selected
    );
    if (selectedCategory) {
      params.category = selectedCategory.value;
    }
    if (state.searchText) {
      params.search = state.searchText;
    }
    return params;
  };

  /**
   * To fetch the list of items to display on Glossary
   */
  async function onFetchGlossary() {
    try {
      dispatch({type: EVENTS.FETCH_LIST_STARTED});
      const glossaryList = await props.fetchGlossaryList(
        getFetchGlossaryParams()
      );
      dispatch({type: EVENTS.FETCH_LIST_SUCCESS, payload: glossaryList});
      if(state.selected){
        onSelectItem(state.selected)
      }
    } catch (error) {
      dispatch({type: EVENTS.FETCH_LIST_FAILED});
    }
  }

  /**
   * To fetch a single item's details for glossary
   */
  async function onFetchGlossaryItem(itemId) {
    try {
      dispatch({type: EVENTS.FETCH_CONTENT_STARTED});
      const glossaryItemContent = await props.fetchGlossaryItem(itemId);
      dispatch({
        type: EVENTS.FETCH_CONTENT_SUCCESS,
        payload: glossaryItemContent,
      });
    } catch (error) {
      dispatch({type: EVENTS.FETCH_CONTENT_FAILED});
    }
  }

  /**
   * Function to handle change in search Input.
   */
  function onSearchInputChange(event) {
    dispatch({type: EVENTS.SEARCH_INPUT_CHANGE, payload: event.target.value});
  }

  /**
   * Function to invoke when a new tab is selected.
   *  Also used for dropdown category selection
   */
  function onTabSelect(newTabValue) {
    dispatch({type: EVENTS.SELECT_NEW_CATEGORY, payload: newTabValue});
  }

  /**
   * Function to invoke when you want to select Glossary Item
   */
  function onSelectItem(itemId) {
    dispatch({type: EVENTS.SELECT_GLOSSARY_LIST_ITEM, payload: itemId});
  }

  /**
   * Function to invoke when you want to select Glossary Item
   */
  function onSelectToolTip(payload) {
    dispatch({type: EVENTS.SELECT_GLOSSARY_TOOLTIP_ITEM, payload: payload});
  }

  /**
   * Function to invoke when you want to collapse or expand index in glossary
   */
  function toggleIndexCollapse() {
    dispatch({type: EVENTS.TOGGLE_GLOSSARY_INDEX_COLLAPSE});
  }

  return {
    state,
    onFetchGlossary,
    onFetchGlossaryItem,
    onSearchInputChange,
    onTabSelect,
    onSelectItem,
    onSelectToolTip,
    toggleIndexCollapse,
  };
}
